import { useCallback, useEffect, useState } from 'react'
import { CATEGORY_VALUE_TO_SLUG } from '../constants/product-categories'

export interface CartItem {
  productId: string
  quantity: number
  name: string
  price: number
  image?: string
  vendorId: string
  vendorName: string
  category: string
  slug: string
}

/** Product detail href for a cart item (matches /products/:category/:slug). */
export function cartItemHref(item: Pick<CartItem, 'category' | 'slug'>): string {
  const categorySlug = CATEGORY_VALUE_TO_SLUG[item.category] || 'other'
  return `/products/${categorySlug}/${item.slug}`
}

const STORAGE_KEY = 'ekubur_cart_v1'
const CART_EVENT = 'ekubur-cart-changed'

function readCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeCart(items: CartItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  window.dispatchEvent(new CustomEvent(CART_EVENT))
}

/**
 * Local cart with cross-page reactivity via a custom event.
 * Single-vendor rule: adding an item from a different vendor replaces
 * the cart (caller confirms via window.confirm before calling addItem
 * when a vendor mismatch is detected).
 */
export function useCart() {
  const [items, setItems] = useState<CartItem[]>(readCart)

  useEffect(() => {
    const sync = () => setItems(readCart())
    window.addEventListener(CART_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(CART_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const addItem = useCallback((item: Omit<CartItem, 'quantity'>, quantity = 1) => {
    const current = readCart()
    const existing = current.find((i) => i.productId === item.productId)
    if (existing) {
      writeCart(
        current.map((i) =>
          i.productId === item.productId ? { ...i, quantity: i.quantity + quantity } : i,
        ),
      )
      return
    }
    // Different vendor → replace the cart (single-vendor checkout)
    const base = current.length && current[0].vendorId !== item.vendorId ? [] : current
    writeCart([...base, { ...item, quantity }])
  }, [])

  const replaceCartWithItem = useCallback((item: Omit<CartItem, 'quantity'>, quantity = 1) => {
    writeCart([{ ...item, quantity }])
  }, [])

  const vendorMismatch = useCallback((vendorId: string) => {
    const current = readCart()
    return current.length > 0 && current[0].vendorId !== vendorId
  }, [])

  const removeItem = useCallback((productId: string) => {
    writeCart(readCart().filter((i) => i.productId !== productId))
  }, [])

  const setQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity < 1) return
    writeCart(
      readCart().map((i) => (i.productId === productId ? { ...i, quantity } : i)),
    )
  }, [])

  const clear = useCallback(() => writeCart([]), [])

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0)
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0)
  const vendorName = items[0]?.vendorName || null

  return {
    items,
    subtotal,
    itemCount,
    vendorName,
    addItem,
    replaceCartWithItem,
    vendorMismatch,
    removeItem,
    setQuantity,
    clear,
  }
}
